import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useCostCurrency } from '../hooks/useCostCurrency'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import ConfirmDialog from '../../schedule/components/ConfirmDialog'
import RecordAttachmentsCard from '../components/RecordAttachmentsCard'
import RecordHistoryCard from '../components/RecordHistoryCard'
import { useInvoiceMutations } from '../hooks/useInvoiceMutations'
import { describeCostControlError, isRecordGoneError } from '../lib/errors'
import { invoiceAffordances } from '../lib/invoices'
import { moneyLocale } from '../lib/money'
import { recordDetailState } from '../lib/records'
import { buildTree } from '../lib/tree'
import type { CostControlView, CostInvoiceDetail, CostInvoiceStatus } from '../types'
import InvoiceDetailSkeleton from './InvoiceDetailSkeleton'
import InvoiceDialog from './InvoiceDialog'
import InvoiceHeader from './InvoiceHeader'
import InvoiceLinesCard from './InvoiceLinesCard'
import InvoiceLinesDialog from './InvoiceLinesDialog'
import InvoiceSummaryCard from './InvoiceSummaryCard'
import PendingSubmittalsAlert from '../components/PendingSubmittalsAlert'
import PaidDialog from './PaidDialog'

const NOT_FOUND = 'CostControl_error_invoice_not_found'
type Pending = { kind: 'status'; to: CostInvoiceStatus } | { kind: 'delete' } | null

const InvoiceDetail: React.FC = () => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { match, history } = useRouter()
  const { projectId, invoiceId } = match.params as { projectId: string; invoiceId: string }
  const actions = useModuleActions('COST_CONTROL', projectId)
  const docControl = useModuleActions('DOC_CONTROL', projectId)
  const { data, loading, error } = useQuery<{ costInvoice: CostInvoiceDetail }>(Query.COST_INVOICE, {
    variables: { id: invoiceId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: viewData } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    skip: !actions.canView,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const currency = useCostCurrency(projectId)
  const [lost, setLost] = useState<{ id: string; message: string } | null>(null)
  const onDetailError = useCallback((id: string, e: unknown) => {
    const message = (e as { message?: string } | null)?.message ?? ''
    if (isRecordGoneError(message, NOT_FOUND)) setLost({ id, message })
  }, [])
  const mutations = useInvoiceMutations(projectId, onDetailError)
  const [editing, setEditing] = useState(false)
  const [linesOpen, setLinesOpen] = useState(false)
  const [paying, setPaying] = useState(false)
  const [pending, setPending] = useState<Pending>(null)
  const tree = useMemo(() => buildTree(viewData?.costControl.nodes ?? []), [viewData])
  const detail = data?.costInvoice
  const message = (lost && lost.id === invoiceId ? lost.message : null) ?? error?.message ?? null
  const state = recordDetailState({
    loading, hasData: !!detail, recordProjectId: detail?.invoice.projectId ?? null, projectId, message,
    rightsLoading: actions.loading, canView: actions.canView, notFoundKey: NOT_FOUND,
  })
  const af = invoiceAffordances(actions, state === 'ready' && detail ? detail.invoice : null)
  // A dialog closes as soon as its action is no longer allowed (3a deferred item).
  useEffect(() => { if (!af.editHeader) setEditing(false) }, [af.editHeader])
  useEffect(() => { if (!af.editLines) setLinesOpen(false) }, [af.editLines])
  useEffect(() => { if (!af.markPaid) setPaying(false) }, [af.markPaid])
  const back = () => history.push(detail?.access === 'REPRESENTATIVE'
    ? `/project/${projectId}/cost-control/contracts/${detail.invoice.contractId}`
    : `/project/${projectId}/cost-control?tab=invoices`)

  if (state === 'loading') return <InvoiceDetailSkeleton />
  if (state !== 'ready' || !detail) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={back}>
          <ArrowLeft className="h-4 w-4" />
          {t('CostControl_invoice_back')}
        </Button>
        <p className="text-sm text-muted-foreground">{state === 'error' ? describeCostControlError(message, t) : t(NOT_FOUND)}</p>
      </div>
    )
  }

  const { invoice } = detail
  const confirmText = !pending ? null
    : pending.kind === 'delete' ? { title: 'CostControl_invoice_delete_title', text: 'CostControl_invoice_delete_text', label: 'Common_delete' }
      : pending.to === 'APPROVED' && invoice.status === 'PENDING' ? { title: 'CostControl_invoice_approve_title', text: 'CostControl_invoice_approve_text', label: 'CostControl_invoice_approve' }
        : pending.to === 'PENDING' ? { title: 'CostControl_invoice_back_to_pending_title', text: 'CostControl_invoice_back_to_pending_text', label: 'CostControl_invoice_back_to_pending' }
          : { title: 'CostControl_invoice_back_to_approved_title', text: 'CostControl_invoice_back_to_approved_text', label: 'CostControl_invoice_back_to_approved' }

  const confirm = async () => {
    if (!pending) return
    if (pending.kind === 'delete') {
      const res = await mutations.remove(invoice.id)
      setPending(null)
      if (res.ok) {
        toastr.success('', t('CostControl_invoice_deleted'))
        back()
      }
      return
    }
    await mutations.setStatus(invoice.id, pending.to, invoice.status)
    setPending(null)
  }

  return (
    <div className="space-y-4">
      <InvoiceHeader
        invoice={invoice}
        af={af}
        onBack={back}
        onEdit={() => setEditing(true)}
        onApprove={() => setPending({ kind: 'status', to: 'APPROVED' })}
        onMarkPaid={() => setPaying(true)}
        onBackToPending={() => setPending({ kind: 'status', to: 'PENDING' })}
        onBackToApproved={() => setPending({ kind: 'status', to: 'APPROVED' })}
        onDelete={() => setPending({ kind: 'delete' })}
      />
      {invoice.status === 'PENDING' && <PendingSubmittalsAlert projectId={projectId} submittals={detail.pendingSubmittals} />}
      <InvoiceSummaryCard invoice={invoice} projectId={projectId} currency={currency} locale={locale} />
      <InvoiceLinesCard invoice={invoice} lines={detail.lines} currency={currency} locale={locale} canEdit={af.editLines} onEdit={() => setLinesOpen(true)} />
      <div className="grid gap-4 lg:grid-cols-2">
        <RecordAttachmentsCard
          projectId={projectId}
          attachments={detail.attachments}
          canAdd={af.addAttachment}
          canRemove={af.removeAttachment}
          canLinkDocuments={docControl.canView}
          onAdd={(source) => mutations.addAttachment(invoice.id, source)}
          onRemove={(attachmentId) => mutations.removeAttachment(invoice.id, attachmentId)}
        />
        <RecordHistoryCard projectId={projectId} entityType="INVOICE" entityId={invoice.id} currency={currency} locale={locale} />
      </div>
      <InvoiceDialog
        open={editing}
        mode="edit"
        invoice={invoice}
        currency={currency}
        onOpenChange={setEditing}
        onSubmit={async (input) => (await mutations.update(invoice.id, input)).ok}
      />
      <InvoiceLinesDialog
        open={linesOpen}
        invoice={invoice}
        lines={detail.lines}
        contractItems={detail.contractItems}
        tree={tree}
        currency={currency}
        locale={locale}
        onOpenChange={setLinesOpen}
        onSave={async (lines) => (await mutations.setLines(invoice.id, lines)).ok}
      />
      <PaidDialog open={paying} onOpenChange={setPaying} onConfirm={async (paidAt) => (await mutations.setStatus(invoice.id, 'PAID', invoice.status, paidAt)).ok} />
      <ConfirmDialog
        open={!!pending}
        title={confirmText ? t(confirmText.title) : ''}
        description={confirmText ? t(confirmText.text) : undefined}
        confirmLabel={confirmText ? t(confirmText.label) : ''}
        variant={pending?.kind === 'delete' ? 'destructive' : 'default'}
        submitting={mutations.saving}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      />
    </div>
  )
}
export default InvoiceDetail
